import axiosInstance from '@/app/api/axiosInstance';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';
import {
  ItemPrice, ItemPriceCreate, ItemPriceUpdate, 
  PriceList, PriceListCreate, PriceListUpdate,
  PriceListItem, PriceListItemCreate, PriceListItemUpdate
} from '@/app/types/pricing';

// Helper to parse numeric fields potentially returned as strings
const parseNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(String(value));
  return isNaN(num) ? null : num;
};

// --- Price List API ---

export const listPriceLists = async (params: ListQueryParams): Promise<PaginatedResponse<PriceList>> => {
  try {
    // Backend filters by org via auth
    const response = await axiosInstance.get<PaginatedResponse<PriceList>>('/price-lists/', { params });
    // Parse numeric fields
    response.data.items = response.data.items.map(pl => ({
      ...pl,
      default_discount_value: parseNumber(pl.default_discount_value) ?? 0,
    }));
    return response.data;
  } catch (error) {
    console.error('Failed to list price lists:', error);
    throw error;
  }
};

export const getPriceList = async (priceListId: number): Promise<PriceList> => {
  try {
    const response = await axiosInstance.get<PriceList>(`/price-lists/${priceListId}`);
     response.data.default_discount_value = parseNumber(response.data.default_discount_value) ?? 0;
     if (response.data.items) {
        response.data.items = response.data.items.map(item => ({
            ...item,
            rate: parseNumber(item.rate),
            discount_percent: parseNumber(item.discount_percent),
            discount_amount: parseNumber(item.discount_amount),
            min_qty: parseNumber(item.min_qty) ?? 0,
            max_qty: parseNumber(item.max_qty),
        }));
     }
    return response.data;
  } catch (error) {
    console.error(`Failed to get price list ID ${priceListId}:`, error);
    throw error;
  }
};

export const createPriceList = async (data: PriceListCreate): Promise<PriceList> => {
  try {
     const payload = {
       ...data,
       // Send numbers as strings if backend expects Decimal as string
       default_discount_value: String(data.default_discount_value ?? 0),
     };
    const response = await axiosInstance.post<PriceList>('/price-lists/', payload);
    response.data.default_discount_value = parseNumber(response.data.default_discount_value) ?? 0;
    return response.data;
  } catch (error) {
    console.error('Failed to create price list:', error);
    throw error;
  }
};

export const updatePriceList = async (priceListId: number, data: PriceListUpdate): Promise<PriceList> => {
  try {
     const payload = {
       ...data,
       ...(data.default_discount_value !== undefined && { default_discount_value: String(data.default_discount_value ?? 0) }),
     };
    const response = await axiosInstance.patch<PriceList>(`/price-lists/${priceListId}`, payload);
    response.data.default_discount_value = parseNumber(response.data.default_discount_value) ?? 0;
    return response.data;
  } catch (error) {
    console.error(`Failed to update price list ID ${priceListId}:`, error);
    throw error;
  }
};

export const deletePriceList = async (priceListId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/price-lists/${priceListId}`);
  } catch (error) {
    console.error(`Failed to delete price list ID ${priceListId}:`, error);
    throw error;
  }
};


// --- Price List Item API ---

export const listPriceListItems = async (priceListId: number, params: ListQueryParams): Promise<PaginatedResponse<PriceListItem>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<PriceListItem>>(`/price-lists/${priceListId}/items/`, { params });
    // Parse numeric fields
    response.data.items = response.data.items.map(item => ({
      ...item,
      rate: parseNumber(item.rate),
      discount_percent: parseNumber(item.discount_percent),
      discount_amount: parseNumber(item.discount_amount),
      min_qty: parseNumber(item.min_qty) ?? 0,
      max_qty: parseNumber(item.max_qty),
    }));
    return response.data;
  } catch (error) {
    console.error(`Failed to list items for price list ID ${priceListId}:`, error);
    throw error;
  }
};

export const addPriceListItem = async (priceListId: number, data: PriceListItemCreate): Promise<PriceListItem> => {
  try {
    const payload = {
      ...data,
      // Send numbers as strings if backend expects Decimal as string
      rate: data.rate !== null && data.rate !== undefined ? String(data.rate) : null,
      discount_percent: data.discount_percent !== null && data.discount_percent !== undefined ? String(data.discount_percent) : null,
      discount_amount: data.discount_amount !== null && data.discount_amount !== undefined ? String(data.discount_amount) : null,
      min_qty: String(data.min_qty ?? 0),
      max_qty: data.max_qty !== null && data.max_qty !== undefined ? String(data.max_qty) : null,
    };
    const response = await axiosInstance.post<PriceListItem>(`/price-lists/${priceListId}/items/`, payload);
    // Parse numeric fields
    response.data.rate = parseNumber(response.data.rate);
    response.data.discount_percent = parseNumber(response.data.discount_percent);
    response.data.discount_amount = parseNumber(response.data.discount_amount);
    response.data.min_qty = parseNumber(response.data.min_qty) ?? 0;
    response.data.max_qty = parseNumber(response.data.max_qty);
    return response.data;
  } catch (error) {
    console.error(`Failed to add item to price list ID ${priceListId}:`, error);
    throw error;
  }
};

export const updatePriceListItem = async (priceListItemId: number, data: PriceListItemUpdate): Promise<PriceListItem> => {
  try {
    const payload = {
      ...data,
      // Send numbers as strings if backend expects Decimal as string
      ...(data.rate !== undefined && { rate: data.rate !== null ? String(data.rate) : null }),
      ...(data.discount_percent !== undefined && { discount_percent: data.discount_percent !== null ? String(data.discount_percent) : null }),
      ...(data.discount_amount !== undefined && { discount_amount: data.discount_amount !== null ? String(data.discount_amount) : null }),
      ...(data.min_qty !== undefined && { min_qty: String(data.min_qty ?? 0) }),
      ...(data.max_qty !== undefined && { max_qty: data.max_qty !== null ? String(data.max_qty) : null }),
    };
    // Use the dedicated endpoint for updating items via their ID
    const response = await axiosInstance.patch<PriceListItem>(`/price-lists/items/${priceListItemId}`, payload);
     // Parse numeric fields
    response.data.rate = parseNumber(response.data.rate);
    response.data.discount_percent = parseNumber(response.data.discount_percent);
    response.data.discount_amount = parseNumber(response.data.discount_amount);
    response.data.min_qty = parseNumber(response.data.min_qty) ?? 0;
    response.data.max_qty = parseNumber(response.data.max_qty);
    return response.data;
  } catch (error) {
    console.error(`Failed to update price list item ID ${priceListItemId}:`, error);
    throw error;
  }
};

export const removePriceListItem = async (priceListItemId: number): Promise<void> => {
  try {
     // Use the dedicated endpoint for deleting items via their ID
    await axiosInstance.delete(`/price-lists/items/${priceListItemId}`);
  } catch (error) {
    console.error(`Failed to remove price list item ID ${priceListItemId}:`, error);
    throw error;
  }
};


// --- Item Price API Functions ---

/**
 * Lists item prices, typically filtered by item_id and price_type.
 */
export const listItemPrices = async (params: ListQueryParams): Promise<PaginatedResponse<ItemPrice>> => {
  try {
    // Support both direct query parameters and filters approach
    const queryParams = { ...params };
    
    // Extract specific filter parameters if they exist
    const item_id = params.item_id;
    const price_type = params.price_type;
    const site_id = params.site_id;
    
    // If filters exist as a string, parse and merge with direct params
    if (params.filters && typeof params.filters === 'string') {
      try {
        const parsedFilters = JSON.parse(params.filters);
        // Merge with direct params, giving priority to direct params
        if (!item_id && parsedFilters.item_id) queryParams.item_id = parsedFilters.item_id;
        if (!price_type && parsedFilters.price_type) queryParams.price_type = parsedFilters.price_type;
        if (!site_id && parsedFilters.site_id) queryParams.site_id = parsedFilters.site_id;
      } catch (e) {
        console.error('Error parsing filters JSON:', e);
      }
    }
    
    const response = await axiosInstance.get<PaginatedResponse<ItemPrice>>('/item-prices/', { 
      params: queryParams 
    });
    
    // Parse numeric fields in the response
    response.data.items = response.data.items.map(ip => ({
      ...ip,
      // Ensure price is number - handle possible string values from backend
      price: typeof ip.price === 'string' ? parseNumber(ip.price) ?? 0 : ip.price,
    }));
    
    return response.data;
  } catch (error) {
    console.error('Failed to list item prices:', error);
    throw error;
  }
};

/**
 * Creates a new item price record.
 */
export const createItemPrice = async (data: ItemPriceCreate): Promise<ItemPrice> => {
  try {
    console.log(`Creating new item price with data:`, data);
    
    // Create payload with explicitly handled price field
    const payload: Record<string, unknown> = { ...data };
    
    // Always convert price to string for consistency
    if (payload.price !== undefined && payload.price !== null) {
      payload.price = String(payload.price); // Send price as string
    }
    
    // Ensure optional fields are sent correctly
    if ('site_id' in data && data.site_id === undefined) delete payload.site_id;
    if ('effective_from' in data && data.effective_from === undefined) delete payload.effective_from;
    if ('effective_to' in data && data.effective_to === undefined) delete payload.effective_to;
    
    console.log(`Prepared create payload:`, payload);
    const response = await axiosInstance.post<ItemPrice>('/item-prices/', payload);
    
    // Parse response
    response.data.price = parseNumber(response.data.price) ?? 0;
    console.log(`Created item price successfully:`, response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to create item price:', error);
    throw error;
  }
};

/**
 * Updates an existing item price record by its ID.
 */
export const updateItemPrice = async (itemPriceId: number, data: ItemPriceUpdate): Promise<ItemPrice> => {
  try {
    console.log(`Updating item price ${itemPriceId} with data:`, data);
    
    // Create payload with explicitly handled price field
    const payload: Record<string, unknown> = { ...data };
    
    // Handle price field specially to ensure correct format
    if (data.price !== undefined) {
      if (data.price === null) {
        // If price is explicitly set to null, keep it null
        payload.price = null;
      } else {
        // Otherwise convert to string (even for zero values)
        payload.price = String(data.price);
      }
    }
    
    // Ensure optional fields are sent correctly if present
    if ('site_id' in data && data.site_id === undefined) delete payload.site_id;
    if ('effective_from' in data && data.effective_from === undefined) delete payload.effective_from;
    if ('effective_to' in data && data.effective_to === undefined) delete payload.effective_to;
    
    console.log(`Prepared payload for item price ${itemPriceId}:`, payload);
    const response = await axiosInstance.patch<ItemPrice>(`/item-prices/${itemPriceId}`, payload);
    
    // Parse response
    response.data.price = parseNumber(response.data.price) ?? 0;
    console.log(`Updated item price ${itemPriceId} successfully:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update item price ID ${itemPriceId}:`, error);
    throw error;
  }
};

/**
 * Deletes an item price record by its ID.
 */
export const deleteItemPrice = async (itemPriceId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/item-prices/${itemPriceId}`);
  } catch (error) {
    console.error(`Failed to delete item price ID ${itemPriceId}:`, error);
    throw error;
  }
};

// --- Bulk Price Update API ---

export interface PriceSheetItem {
  item_id: number;
  item_name: string;
  item_code: string;
  price: string;
}

export interface BulkPriceUpdateRequest {
  price_type: 'sale' | 'purchase' | 'mrp' | 'cost';
  site_id?: number | null;
  is_tax_inclusive?: boolean;
  items: Array<{
    item_id: number;
    price: number;
  }>;
}

export interface BulkPriceUpdateResponse {
  total_items: number;
  created: number;
  updated: number;
  errors: Array<{
    item_id: number;
    error: string;
  }>;
}

/**
 * Fetches price sheet for all items with their current prices
 */
export const getPriceSheet = async (params: {
  price_type: 'sale' | 'purchase' | 'mrp' | 'cost';
  site_id?: number | null;
}): Promise<PriceSheetItem[]> => {
  try {
    const queryParams: any = { price_type: params.price_type };
    if (params.site_id) {
      queryParams.site_id = params.site_id;
    }
    
    const response = await axiosInstance.get<PriceSheetItem[]>('/item-prices/sheet', {
      params: queryParams
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to fetch price sheet:', error);
    throw error;
  }
};

/**
 * Bulk updates item prices
 */
export const bulkUpdateItemPrices = async (data: BulkPriceUpdateRequest): Promise<BulkPriceUpdateResponse> => {
  try {
    const response = await axiosInstance.post<BulkPriceUpdateResponse>('/item-prices/bulk-update', data);
    return response.data;
  } catch (error) {
    console.error('Failed to bulk update prices:', error);
    throw error;
  }
}; 